import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Award, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Eye, 
  Edit, 
  Copy, 
  Download, 
  Trash2, 
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Star,
  FileText,
  AlertCircle,
  TrendingUp,
  Calendar,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { SubmissionPreview } from '@/components/SubmissionPreview';
import jsPDF from 'jspdf';

const STATUS_CONFIG = {
  Draft: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileText, iconColor: 'text-yellow-500' },
  Submitted: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: CheckCircle, iconColor: 'text-blue-500' },
  'Under Review': { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Clock, iconColor: 'text-purple-500' },
  Finalist: { color: 'bg-green-100 text-green-700 border-green-300', icon: Star, iconColor: 'text-green-500' },
  'Not Selected': { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, iconColor: 'text-red-500' },
  Winner: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Trophy, iconColor: 'text-yellow-600' }
};

const CATEGORIES = [
  { id: 'sustainable', title: 'Best Sustainable Stand' },
  { id: 'technology', title: 'Best Use of Technology' },
  { id: 'visitor_experience', title: 'Best Visitor Experience' },
  { id: 'judges_choice', title: "Judge's Choice Award" },
  { id: 'brand_storytelling', title: 'Best Brand Experience & Storytelling' },
  { id: 'innovation', title: 'Most Innovative Stand Concept' },
  { id: 'structural', title: 'Best Structural Design & Engineering' },
  { id: 'creative', title: 'Best Creative Concept' }
];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showPreview, setShowPreview] = useState(false);
  const [previewSubmission, setPreviewSubmission] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusChangeSubmission, setStatusChangeSubmission] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  // Check authentication
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please sign in to view your dashboard');
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Fetch submissions
  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!user) return;
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/submissions`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch submissions');
        }
        
        const data = await response.json();
        setSubmissions(data);
        setFilteredSubmissions(data);
      } catch (error) {
        console.error('Error fetching submissions:', error);
        toast.error('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubmissions();
  }, [user]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...submissions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sub => {
        const projectName = sub.project_details?.project_name || sub.category_specific_data?.project_name || '';
        return projectName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(sub => sub.category === categoryFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.submitted_at) - new Date(a.submitted_at);
      } else if (sortBy === 'edited') {
        return new Date(b.updated_at) - new Date(a.updated_at);
      } else if (sortBy === 'category') {
        return (a.category || '').localeCompare(b.category || '');
      }
      return 0;
    });

    setFilteredSubmissions(filtered);
  }, [submissions, searchTerm, statusFilter, categoryFilter, sortBy]);

  // Statistics
  const stats = {
    total: submissions.length,
    draft: submissions.filter(s => s.status === 'Draft').length,
    submitted: submissions.filter(s => s.status === 'Submitted').length,
    underReview: submissions.filter(s => s.status === 'Under Review').length,
    finalist: submissions.filter(s => s.status === 'Finalist').length,
    notSelected: submissions.filter(s => s.status === 'Not Selected').length,
    winner: submissions.filter(s => s.status === 'Winner').length
  };

  // Calculate completion percentage
  const calculateCompletion = (submission) => {
    const requiredFields = [
      submission.basic_info?.first_name,
      submission.basic_info?.email,
      submission.category,
      submission.category_specific_data
    ];
    
    const filled = requiredFields.filter(field => field && (typeof field !== 'object' || Object.keys(field).length > 0)).length;
    return Math.round((filled / requiredFields.length) * 100);
  };
  
  // Get completion bar color
  const getCompletionColor = (percentage) => {
    if (percentage === 100) return 'bg-green-500';
    if (percentage >= 67) return 'bg-orange-500';
    if (percentage >= 34) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  // Get project name or default
  const getProjectName = (submission) => {
    const projectName = submission.project_details?.project_name || 
                       submission.category_specific_data?.project_name ||
                       submission.basic_info?.company;
    
    if (projectName) return projectName;
    
    // Return "Untitled - [Category Name]"
    return `Untitled - ${submission.category}`;
  };
  
  // Check if user is admin (simple email check - can be expanded later)
  const isAdmin = user?.email?.toLowerCase() === 'admin@standvision.com' || user?.email?.toLowerCase() === 'mbucak@gmail.com';

  // Handle actions
  const handlePreview = async (submission) => {
    const category = CATEGORIES.find(c => c.title === submission.category);
    if (!category) {
      console.error('Category not found:', submission.category);
      toast.error('Unable to preview: category not found');
      return;
    }

    const formData = {
      selectedCategories: [category.id],
      firstName: submission.basic_info?.firstName || '',
      lastName: submission.basic_info?.lastName || '',
      email: submission.basic_info?.email || '',
      phone: submission.basic_info?.phone || '',
      company: submission.basic_info?.company || '',
      country: submission.basic_info?.country || '',
      categorySpecificData: {
        [category.id]: submission.category_specific_data || {}
      }
    };

    setPreviewSubmission({ formData, category, submissionId: submission.id });
    setShowPreview(true);
  };

  const handleEdit = (submissionId) => {
    navigate(`/apply?id=${submissionId}`);
  };

  const handleDuplicate = async (submission) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const payload = {
        ...submission,
        id: undefined,
        status: 'Draft',
        submitted_at: undefined,
        updated_at: undefined
      };
      
      delete payload.id;
      delete payload.submitted_at;
      delete payload.updated_at;

      const response = await fetch(`${backendUrl}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to duplicate');
      
      toast.success('Submission duplicated successfully');
      window.location.reload();
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate submission');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/submissions/${submissionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Submission deleted');
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleDownloadPDF = async (submission) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let y = margin;
      
      // Helper function to add text with wrapping
      const addText = (text, fontSize = 10, isBold = false) => {
        doc.setFontSize(fontSize);
        doc.setFont(undefined, isBold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
        
        // Check if we need a new page
        if (y + (lines.length * fontSize * 0.35) > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        
        doc.text(lines, margin, y);
        y += lines.length * fontSize * 0.35 + 5;
      };
      
      const addSection = (title) => {
        y += 5;
        doc.setFillColor(245, 158, 11); // Orange
        doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin + 3, y + 6);
        doc.setTextColor(0, 0, 0);
        y += 15;
      };
      
      // Title
      doc.setFillColor(245, 158, 11);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Stand Vision Awards - Submission', pageWidth / 2, 20, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y = 40;
      
      // Status
      addText(`Status: ${submission.status}`, 11, true);
      addText(`Submission ID: ${submission.id}`, 9);
      addText(`Submitted: ${new Date(submission.submitted_at).toLocaleDateString('en-US')}`, 9);
      if (submission.updated_at) {
        addText(`Last Updated: ${new Date(submission.updated_at).toLocaleDateString('en-US')}`, 9);
      }
      
      // Basic Information
      addSection('BASIC INFORMATION');
      addText(`Name: ${submission.basic_info.firstName} ${submission.basic_info.lastName}`, 10, true);
      addText(`Email: ${submission.basic_info.email}`);
      addText(`Phone: ${submission.basic_info.phone}`);
      addText(`Company: ${submission.basic_info.company}`);
      addText(`Country: ${submission.basic_info.country}`);
      
      // Category
      addSection('CATEGORY');
      addText(submission.category, 11, true);
      
      // Category Specific Data
      if (submission.category_specific_data) {
        addSection('SUBMISSION DETAILS');
        
        Object.entries(submission.category_specific_data).forEach(([key, value]) => {
          if (key === 'photos' || key === 'photo' || !value) return;
          
          const label = key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          if (typeof value === 'string' && value.length > 0) {
            addText(`${label}:`, 10, true);
            addText(value, 9);
            y += 3;
          }
        });
      }
      
      // Project Details (Best Creative Concept)
      if (submission.project_details) {
        addSection('PROJECT DETAILS');
        if (submission.project_details.project_name) {
          addText(`Project Name: ${submission.project_details.project_name}`, 10, true);
        }
        if (submission.project_details.exhibition_info) {
          addText(`Exhibition: ${submission.project_details.exhibition_info}`);
        }
        if (submission.project_details.stand_area) {
          addText(`Stand Area: ${submission.project_details.stand_area} m²`);
        }
        if (submission.project_details.budget_range) {
          addText(`Budget Range: ${submission.project_details.budget_range}`);
        }
      }
      
      // Concept & Design
      if (submission.concept_design) {
        addSection('CONCEPT & DESIGN');
        Object.entries(submission.concept_design).forEach(([key, value]) => {
          if (value) {
            const label = key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            addText(`${label}:`, 10, true);
            addText(value, 9);
            y += 3;
          }
        });
      }
      
      // Technical Information
      if (submission.technical_info) {
        addSection('TECHNICAL INFORMATION');
        Object.entries(submission.technical_info).forEach(([key, value]) => {
          if (value) {
            const label = key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            addText(`${label}:`, 10, true);
            addText(value, 9);
            y += 3;
          }
        });
      }
      
      // Impact & Results
      if (submission.impact_results) {
        addSection('IMPACT & RESULTS');
        Object.entries(submission.impact_results).forEach(([key, value]) => {
          if (value) {
            const label = key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            addText(`${label}:`, 10, true);
            addText(value, 9);
            y += 3;
          }
        });
      }
      
      // Team Information
      if (submission.team_info) {
        addSection('TEAM INFORMATION');
        Object.entries(submission.team_info).forEach(([key, value]) => {
          if (value) {
            const label = key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            addText(`${label}:`, 10, true);
            addText(value, 9);
            y += 3;
          }
        });
      }
      
      // Photos note
      const photos = submission.category_specific_data?.photos || [];
      if (photos.length > 0) {
        addSection('VISUAL DOCUMENTATION');
        addText(`This submission includes ${photos.length} photo(s).`);
        addText('Photos are not included in this PDF but can be viewed online.', 9);
      }
      
      // Video links
      if (submission.category_specific_data?.video_link) {
        y += 5;
        addText('Video Link:', 10, true);
        addText(submission.category_specific_data.video_link, 9);
      }
      
      if (submission.category_specific_data?.virtual_tour_url) {
        y += 5;
        addText('Virtual Tour:', 10, true);
        addText(submission.category_specific_data.virtual_tour_url, 9);
      }
      
      // Footer
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${totalPages} | Stand Vision Awards | Generated: ${new Date().toLocaleDateString('en-US')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
      
      // Save
      const fileName = `submission_${submission.category.replace(/\s+/g, '_')}_${submission.basic_info.lastName}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleChangeStatus = async () => {
    if (!newStatus || !statusChangeSubmission) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Update status locally (since we don't have a status update endpoint yet)
      const updatedSubmissions = submissions.map(s => 
        s.id === statusChangeSubmission.id ? { ...s, status: newStatus } : s
      );
      setSubmissions(updatedSubmissions);
      
      toast.success(`Status changed to ${newStatus}`);
      setShowStatusDialog(false);
      setStatusChangeSubmission(null);
      setNewStatus('');
    } catch (error) {
      console.error('Status change error:', error);
      toast.error('Failed to change status');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedSubmissions.length} submissions?`)) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await Promise.all(
        selectedSubmissions.map(id =>
          fetch(`${backendUrl}/api/submissions/${id}`, {
            method: 'DELETE',
            credentials: 'include'
          })
        )
      );
      
      toast.success(`${selectedSubmissions.length} submissions deleted`);
      setSubmissions(prev => prev.filter(s => !selectedSubmissions.includes(s.id)));
      setSelectedSubmissions([]);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete submissions');
    }
  };

  const toggleSelection = (id) => {
    setSelectedSubmissions(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSubmissions.length === filteredSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(filteredSubmissions.map(s => s.id));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-orange-500" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Notification banner
  const deadline = new Date('2025-12-15T23:59:59Z');
  const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">My Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user.name || user.email}</p>
        </div>

        {/* Notification Banner */}
        {daysLeft > 0 && daysLeft <= 30 && (
          <Card className="mb-6 p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-900">Submission Deadline Approaching</h3>
                <p className="text-sm text-amber-700">
                  {daysLeft} days left to submit your entries for Stand Vision Awards 2025
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Statistics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total</span>
              <Award className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </Card>
          
          {Object.entries({
            Draft: stats.draft,
            Submitted: stats.submitted,
            'Under Review': stats.underReview,
            Finalist: stats.finalist,
            'Not Selected': stats.notSelected,
            Winner: stats.winner
          }).map(([status, count]) => {
            const StatusIcon = STATUS_CONFIG[status].icon;
            const iconColor = STATUS_CONFIG[status].iconColor;
            return (
              <Card key={status} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">{status}</span>
                  <StatusIcon className={`h-4 w-4 ${iconColor}`} />
                </div>
                <p className="text-2xl font-bold">{count}</p>
              </Card>
            );
          })}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <Button
            onClick={() => navigate('/apply')}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Submission
          </Button>

          {selectedSubmissions.length > 0 && (
            <div className="flex gap-2">
              <Button
                onClick={handleBulkDelete}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {selectedSubmissions.length} Selected
              </Button>
            </div>
          )}
        </div>

        {/* Filters & Search */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by project name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Submitted">Submitted</SelectItem>
                <SelectItem value="Under Review">Under Review</SelectItem>
                <SelectItem value="Finalist">Finalist</SelectItem>
                <SelectItem value="Not Selected">Not Selected</SelectItem>
                <SelectItem value="Winner">Winner</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Submission Date</SelectItem>
                <SelectItem value="edited">Last Edited</SelectItem>
                <SelectItem value="category">Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Empty State */}
        {filteredSubmissions.length === 0 && submissions.length === 0 && (
          <Card className="p-12 text-center">
            <Award className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No Submissions Yet</h2>
            <p className="text-gray-600 mb-6">Start your journey to recognition by submitting your first entry</p>
            <Button
              onClick={() => navigate('/apply')}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Submission
            </Button>
            
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {CATEGORIES.slice(0, 4).map(cat => (
                <div key={cat.id} className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-sm mb-1">{cat.title}</h3>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* No Results from Filter */}
        {filteredSubmissions.length === 0 && submissions.length > 0 && (
          <Card className="p-12 text-center">
            <Filter className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">No Results Found</h2>
            <p className="text-gray-600 mb-4">Try adjusting your filters or search term</p>
            <Button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCategoryFilter('all');
              }}
              variant="outline"
            >
              Clear All Filters
            </Button>
          </Card>
        )}

        {/* Submissions Grid */}
        {filteredSubmissions.length > 0 && (
          <>
            {/* Select All */}
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                checked={selectedSubmissions.length === filteredSubmissions.length}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm text-gray-600">
                {selectedSubmissions.length > 0 
                  ? `${selectedSubmissions.length} selected`
                  : 'Select all'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSubmissions.map((submission) => {
                const category = CATEGORIES.find(c => c.id === submission.category);
                const statusConfig = STATUS_CONFIG[submission.status] || STATUS_CONFIG.Submitted;
                const StatusIcon = statusConfig.icon;
                const completion = calculateCompletion(submission);
                const completionColor = getCompletionColor(completion);
                const projectName = getProjectName(submission);

                return (
                  <Card key={submission.id} className="p-6 hover:shadow-xl hover:scale-[1.02] transition-all duration-200 hover:border-orange-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        <Checkbox
                          checked={selectedSubmissions.includes(submission.id)}
                          onCheckedChange={() => toggleSelection(submission.id)}
                        />
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{projectName}</h3>
                          <p className="text-sm text-gray-600">{category?.title || submission.category}</p>
                        </div>
                      </div>

                      {/* Quick Actions Menu */}
                      <div className="relative">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === submission.id ? null : submission.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="h-5 w-5 text-gray-600" />
                        </button>
                        
                        {actionMenuOpen === submission.id && (
                          <div className="absolute right-0 top-10 bg-white shadow-lg rounded-lg border border-gray-200 py-2 z-10 min-w-[180px]">
                            <button
                              onClick={() => {
                                handlePreview(submission);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            >
                              <Eye className="h-4 w-4" /> Preview
                            </button>
                            <button
                              onClick={() => {
                                handleEdit(submission.id);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            >
                              <Edit className="h-4 w-4" /> Edit
                            </button>
                            <button
                              onClick={() => {
                                handleDuplicate(submission);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            >
                              <Copy className="h-4 w-4" /> Duplicate
                            </button>
                            <button
                              onClick={() => {
                                handleDownloadPDF(submission);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                            >
                              <Download className="h-4 w-4" /> Download PDF
                            </button>
                            {isAdmin && (
                              <>
                                <div className="border-t border-gray-200 my-1" />
                                <button
                                  onClick={() => {
                                    setStatusChangeSubmission(submission);
                                    setNewStatus(submission.status);
                                    setShowStatusDialog(true);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm text-blue-600"
                                >
                                  <RefreshCw className="h-4 w-4" /> Change Status
                                </button>
                              </>
                            )}
                            <div className="border-t border-gray-200 my-1" />
                            <button
                              onClick={() => {
                                handleDelete(submission.id);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-sm text-red-600"
                            >
                              <Trash2 className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {submission.status}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Completion</span>
                        <span className="font-medium">{completion}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`${completionColor} h-2 rounded-full transition-all`}
                          style={{ width: `${completion}%` }}
                        />
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        Edited: {new Date(submission.updated_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handlePreview(submission)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        onClick={() => handleEdit(submission.id)}
                        size="sm"
                        className="flex-1 bg-orange-500 hover:bg-orange-600"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Status Change Dialog */}
      {showStatusDialog && statusChangeSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Change Submission Status</h3>
            <p className="text-sm text-gray-600 mb-4">
              Change status for: <span className="font-medium">{getProjectName(statusChangeSubmission)}</span>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Submitted">Submitted</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Finalist">Finalist</SelectItem>
                  <SelectItem value="Not Selected">Not Selected</SelectItem>
                  <SelectItem value="Winner">Winner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowStatusDialog(false);
                  setStatusChangeSubmission(null);
                  setNewStatus('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangeStatus}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Status
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewSubmission && (
        <SubmissionPreview
          formData={previewSubmission.formData}
          category={previewSubmission.category}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setPreviewSubmission(null);
          }}
          onEdit={() => {
            setShowPreview(false);
            if (previewSubmission.submissionId) {
              handleEdit(previewSubmission.submissionId);
            }
          }}
          onSubmit={() => {
            toast.info('Cannot submit from preview in dashboard');
          }}
          submitting={false}
        />
      )}
    </div>
  );
};

export default DashboardPage;
